/**
 * Per-leg travel aggregation (arrival / during-session / departure) — pure,
 * framework-agnostic, shared by the frontend and the lambdas.
 *
 * Owner spec (2026-10-07 field feedback): session travel is THREE legs, each
 * with km + duration, each shown only when it genuinely occurred:
 *
 * 1. **Arrival** — the en-route journey (explicit "start journey" →
 *    explicit arrival / check-in). Aggregated from the persisted journey
 *    pings, which survive checkout (#399 closes the journey without
 *    discarding them).
 * 2. **During** — the check-in → check-out GPS track (today's only km
 *    number). A stationary in-home session has no leg here — that is
 *    correct, not missing data.
 * 3. **Departure** — NOT tracked (owner rule revision, 2026-10-07, which
 *    retired the live-tracked post-checkout leg): *"the departure travel
 *    will be ASSUMED as the same as arrival, UNLESS the provider is going to
 *    another session — then the departure travel is equal to the next
 *    session arrival."* See {@link resolveDeparture}.
 *
 * HONESTY POSTURE (same as #420 billable km): every TRACKED figure is
 * re-derived from segment evidence with `applyBillableSanity` — teleports,
 * untracked gaps and jitter runs never count. Tracked km/time is a LOWER
 * BOUND; while the app is backgrounded there are no fixes and nothing is
 * guessed. Server never trusts client aggregates. The departure figure is an
 * ASSUMPTION by design (the owner chose assumption over measurement) and is
 * always labelled as such in the UI and the clipboard block.
 *
 * PER-LEG DURATION SEMANTICS (documented decision):
 * - **Arrival** uses the explicit bookends `startedAt → arrivedAt` — the
 *   provider literally pressed "start journey" when leaving and
 *   "I've arrived" (or checked in) on arrival, so the whole span IS travel.
 *   Clamped to check-in when a stale journey was only closed later, and
 *   discarded when insane (> MAX_SANE_TRAVEL_DURATION_MS).
 * - **During** has no explicit travel bookends, so its duration is the summed
 *   duration of BILLABLE (driving) segments — time actually moving. A support
 *   worker who drives 10 min to a community event, stays an hour, and drives
 *   10 min back gets ~20 min, not 80.
 *
 * DEPARTURE RESOLUTION RULE (documented decision — see resolveDeparture):
 * - **Linked** (`next-session`): the provider started ANOTHER session's
 *   arrival journey within {@link DEPARTURE_LINK_WINDOW_MS} (90 min) after
 *   this session's check-out → this session's departure IS that A→B journey,
 *   shown here as informational/cross-referenced and counted ONCE — as the
 *   next session's billable arrival.
 * - **Assumed** (`assumed`): otherwise, departure = this session's own
 *   arrival leg, labelled "(assumed, same as arrival)". No arrival leg ⇒ no
 *   assumption ⇒ no departure shown at all.
 */

import {
  applyBillableSanity,
  buildSegments,
  sumBillableDrivingKm,
} from './km'
import { isSaneTravelDurationMs } from './journey'
import { formatClockTime } from './time-format'
import type {
  DepartureResolution,
  DriveSegment,
  GeoStamp,
  Session,
  SessionJourney,
  SessionTravel,
  TravelLegAggregate,
} from './types'

/** Arrival leg recorded > 1 km from the address: zero billable km and minutes. */
export const REMOTE_ARRIVAL_EXCLUSION_REASON = 'remote_arrival' as const

export const REMOTE_ARRIVAL_EXCLUSION_LABEL =
  'Recorded more than 1 km from the address — arrival travel excluded'

/** Zeroed arrival leg for a remote (> 1 km) check-in. */
export function excludedRemoteArrivalLeg(): TravelLegAggregate {
  return {
    km: 0,
    durationMs: 0,
    excluded: true,
    exclusionReason: REMOTE_ARRIVAL_EXCLUSION_REASON,
  }
}

export function isExcludedTravelLeg(
  leg: TravelLegAggregate | null | undefined,
): boolean {
  return Boolean(leg?.excluded)
}

/* ----------------------------- aggregation -------------------------------- */

/** Summed duration (ms) of billable segments after sanity re-derivation. */
function billableMovement(segments: DriveSegment[]): { km: number; durationMs: number } {
  const sane = applyBillableSanity(segments)
  let durationMs = 0
  for (const segment of sane) {
    if (!segment.billable) continue
    durationMs += Math.max(0, segment.to.timestamp - segment.from.timestamp)
  }
  return { km: sumBillableDrivingKm(sane), durationMs }
}

/**
 * Aggregate a check-in→check-out or departure track into a leg.
 * Returns null when nothing billable was tracked — the leg did not occur.
 */
export function aggregateSegmentsLeg(
  segments: DriveSegment[] | null | undefined,
): TravelLegAggregate | null {
  if (!segments || segments.length === 0) return null
  const { km, durationMs } = billableMovement(segments)
  if (km <= 0) return null
  return { km, durationMs }
}

/**
 * Split a check-in → check-out GPS track at the recorded departure time.
 *
 * Normally `actualEnd` is the moment the provider pressed "Record departure"
 * and every segment precedes it, so `departure` is empty. When the departure
 * time was BACKDATED to the geofence exit (2026-10-08 field test: the
 * provider drove off without recording and only confirmed later), the
 * segments after it are the drive away — they must NOT inflate the during
 * leg, which would then be double-counted against the assumed departure.
 * Those segments go to the departure leg as evidence instead.
 *
 * A segment ENDING at or after `actualEnd` is movement that finished after
 * leaving — including the segment from the last inside fix to the first
 * outside fix, which IS the departure — so it goes to departure and the
 * during leg never grows past the recorded end. Pure.
 */
export function splitSegmentsAtDeparture(
  segments: readonly DriveSegment[] | null | undefined,
  actualEnd: number | null | undefined,
): { during: DriveSegment[]; departure: DriveSegment[] } {
  const all = segments ?? []
  if (actualEnd == null) return { during: [...all], departure: [] }
  const during: DriveSegment[] = []
  const departure: DriveSegment[] = []
  for (const segment of all) {
    if (segment.to.timestamp < actualEnd) during.push(segment)
    else departure.push(segment)
  }
  return { during, departure }
}

/**
 * A GPS-silent stretch of the in-session window longer than this could hide a
 * real drive — the during-leg row must not claim "no travel" as fact then.
 * 5 min is comfortably longer than sampling hiccups and shorter than any
 * community-access drive worth billing.
 */
export const DURING_GPS_BLIND_GAP_MS = 5 * 60_000

export type DuringTrackingCoverage = 'complete' | 'partial' | 'none'

/**
 * How much of the check-in → check-out window the GPS track actually
 * witnessed (2026-10-07 field feedback "looks like during-session travel
 * wasn't picked up" — the row must distinguish *no travel happened* from
 * *travel could not be seen*):
 *
 * - `'complete'`: samples span the window with no blind gap — a "no travel"
 *   claim is evidence-backed.
 * - `'partial'`: there is at least one blind stretch > {@link DURING_GPS_BLIND_GAP_MS}
 *   (app backgrounded / GPS off) — travel in it would have been missed.
 * - `'none'`: no usable track at all for the window.
 *
 * Pure; uses the raw stored segments (ALL of them, not just billable — idle
 * and walking segments are proof the GPS was watching).
 *
 * A blind stretch is measured TWO ways: the hole between consecutive
 * segments, and the span INSIDE a single segment. The second matters most in
 * production: `buildSegments` chains consecutive fixes, so when GPS goes
 * silent the silence is the `from → to` span of ONE segment, not a hole
 * between two (2026-10-08 field test: a 420 s locked-phone gap was flagged
 * TIME_GAP server-side, yet the card read as fully tracked because only
 * inter-segment holes were checked).
 */
export function assessDuringTrackingCoverage(session: {
  actualStart?: number | null
  actualEnd?: number | null
  driveSegments?: DriveSegment[] | null
}): DuringTrackingCoverage {
  const start = session.actualStart ?? null
  const end = session.actualEnd ?? null
  if (start == null || end == null || end <= start) return 'none'
  const inWindow = (session.driveSegments ?? []).filter(
    (segment) => segment.to.timestamp > start && segment.from.timestamp < end,
  )
  if (inWindow.length === 0) return 'none'
  const sorted = [...inWindow].sort((a, b) => a.from.timestamp - b.from.timestamp)
  let witnessedUpTo = start
  let maxBlindGapMs = 0
  for (const segment of sorted) {
    maxBlindGapMs = Math.max(maxBlindGapMs, segment.from.timestamp - witnessedUpTo)
    // The sample-to-sample span itself: no fix arrived in between.
    maxBlindGapMs = Math.max(maxBlindGapMs, segment.to.timestamp - segment.from.timestamp)
    witnessedUpTo = Math.max(witnessedUpTo, segment.to.timestamp)
  }
  maxBlindGapMs = Math.max(maxBlindGapMs, end - witnessedUpTo)
  return maxBlindGapMs > DURING_GPS_BLIND_GAP_MS ? 'partial' : 'complete'
}

/**
 * Aggregate the arrival leg from the persisted journey blob.
 *
 * km: billable-sane driving distance across the GPS-sourced pings (estimate
 * pings reuse stale coordinates and are excluded). Pings arrive at ~60 s
 * cadence capped at MAX_JOURNEY_PINGS with the middle trimmed on long drives,
 * so long journeys undercount across the trimmed gap — lower bound, never
 * padded.
 *
 * duration: `startedAt → arrivedAt` (explicit provider actions), clamped to
 * check-in time when the journey was only closed later, and discarded when
 * insane. Returns null when the journey never happened.
 */
export function aggregateArrivalLeg(
  journey: SessionJourney | null | undefined,
  options?: { checkInAt?: number | null },
): TravelLegAggregate | null {
  if (!journey || journey.status === 'none' || journey.startedAt == null) return null

  const gpsSamples: GeoStamp[] = journey.pings
    .filter((ping) => ping.locationSource !== 'estimate')
    .map((ping) => ({ lat: ping.lat, lng: ping.lng, timestamp: ping.timestamp }))
  const { km } = billableMovement(buildSegments(gpsSamples))

  let endedAt = journey.arrivedAt ?? null
  const checkInAt = options?.checkInAt ?? null
  if (checkInAt != null && (endedAt == null || checkInAt < endedAt)) endedAt = checkInAt

  let durationMs = 0
  if (endedAt != null && endedAt > journey.startedAt) {
    const span = endedAt - journey.startedAt
    if (isSaneTravelDurationMs(span)) durationMs = span
  }

  if (km <= 0 && durationMs <= 0) return null
  return { km, durationMs }
}

/* -------------------------- departure resolution --------------------------- */

/**
 * Linkage window: session A's departure is linked to session B's arrival
 * journey only when B's journey STARTED within this long after A's check-out.
 *
 * WHY 90 MINUTES: the link should only fire when the provider went (more or
 * less) straight on to the next session. A generous window would mis-attribute
 * — e.g. provider checks out at 10 am, goes home, and drives to a 4 pm session:
 * that afternoon journey is NOT the morning session's departure. 90 min covers
 * a fuel stop / lunch between back-to-back sessions while staying well inside
 * "the same run of sessions"; it also makes the linkage same-day by
 * construction, so no separate calendar-day check is needed.
 */
export const DEPARTURE_LINK_WINDOW_MS = 90 * 60 * 1000

/** A session's arrival leg: the stored aggregate, else derived from evidence. */
export function resolveArrivalLeg(
  session: Pick<Session, 'travel' | 'journey' | 'actualStart' | 'arrivalLocationFlag'>,
): TravelLegAggregate | null {
  if (session.arrivalLocationFlag != null || session.travel?.arrival?.excluded) {
    return session.travel?.arrival?.excluded
      ? session.travel.arrival
      : excludedRemoteArrivalLeg()
  }
  return (
    session.travel?.arrival ??
    aggregateArrivalLeg(session.journey ?? null, { checkInAt: session.actualStart ?? null })
  )
}

function isCancelledForLinkage(session: Session): boolean {
  return session.lifecycleState === 'cancelled' || session.coreStatus === 'cancelled'
}

/**
 * Resolve a session's DEPARTURE figure (owner rule, 2026-10-07 revision):
 *
 * 1. **Linked** — the earliest other session B whose arrival journey started
 *    within {@link DEPARTURE_LINK_WINDOW_MS} after A's check-out, provided
 *    B is not cancelled, B's journey produced a real arrival leg, and no
 *    OTHER session of this provider checked out between A's check-out and
 *    B's journey start (that session would be the journey's true origin —
 *    one journey must never be claimed as two sessions' departures).
 *    Resolution: `next-session`, carrying B's arrival leg (informational on
 *    A; the km bill once, as B's arrival).
 * 2. **Assumed** — otherwise, A's own arrival leg (`assumed`). If A has no
 *    arrival leg (journey never ran), there is NO departure resolution.
 *
 * Pure and recomputed on every read, so a linked B being cancelled later
 * automatically falls back to assumed, and a B booked-but-never-journeyed
 * never links in the first place. Only resolves for finished sessions
 * (`checked-out` / `synced`).
 */
export function resolveDeparture(
  session: Session,
  candidates: readonly Session[],
): DepartureResolution | null {
  if (session.lifecycleState !== 'checked-out' && session.lifecycleState !== 'synced') return null

  const checkOutAt = session.actualEnd ?? null
  if (checkOutAt != null) {
    let linked: { session: Session; journeyStartedAt: number; leg: TravelLegAggregate } | null = null
    for (const candidate of candidates) {
      if (candidate.uuid === session.uuid) continue
      if (isCancelledForLinkage(candidate)) continue
      const journeyStartedAt = candidate.journey?.startedAt ?? null
      if (journeyStartedAt == null) continue
      if (journeyStartedAt < checkOutAt) continue
      if (journeyStartedAt - checkOutAt > DEPARTURE_LINK_WINDOW_MS) continue
      const leg = resolveArrivalLeg(candidate)
      if (!leg || leg.excluded) continue
      if (!linked || journeyStartedAt < linked.journeyStartedAt) {
        linked = { session: candidate, journeyStartedAt, leg }
      }
    }
    if (linked) {
      // The journey belongs to the MOST RECENT check-out before it: if some
      // other session checked out in between, A is not the journey's origin.
      const linkedStartedAt = linked.journeyStartedAt
      const interceded = candidates.some(
        (other) =>
          other.uuid !== session.uuid &&
          other.uuid !== linked!.session.uuid &&
          !isCancelledForLinkage(other) &&
          other.actualEnd != null &&
          other.actualEnd > checkOutAt &&
          other.actualEnd <= linkedStartedAt,
      )
      if (!interceded) {
        return {
          kind: 'next-session',
          leg: linked.leg,
          nextSessionUuid: linked.session.uuid,
          nextSessionStart: linked.session.start,
        }
      }
    }
  }

  const arrival = resolveArrivalLeg(session)
  // A remote arrival's zeroed/excluded leg is not an honest assumption for
  // the drive home — don't invent a billed departure from it.
  if (!arrival || arrival.excluded) return null
  return { kind: 'assumed', leg: arrival }
}

/**
 * Inject the read-time departure resolution into every session of a provider's
 * list (the list itself is the candidate set — linkage is same-day by
 * construction, so any window that includes a finished session includes its
 * next session too). Used by the list-my-sessions provider lens and the mock
 * repository; member payloads never carry `travel` at all.
 */
export function applyDepartureResolutions(sessions: readonly Session[]): Session[] {
  return sessions.map((session) => {
    const departureResolution = resolveDeparture(session, sessions)
    if (!departureResolution && !session.travel) return session
    return { ...session, travel: { ...(session.travel ?? {}), departureResolution } }
  })
}

/**
 * True when a travel breakdown has at least one real leg to show. The legacy
 * tracked `departure` leg deliberately does NOT count — it is stored evidence
 * from old phone builds, no longer displayed.
 */
export function hasAnyTravelLeg(travel: SessionTravel | null | undefined): boolean {
  if (!travel) return false
  return Boolean(travel.arrival || travel.during || travel.departureResolution)
}

/* ------------------------------ formatting -------------------------------- */

/** "5.6 km · 14 min" (duration omitted when not tracked). */
export function formatTravelLeg(leg: TravelLegAggregate): string {
  if (leg.excluded) return '0.0 km · excluded'
  const km = `${leg.km.toFixed(1)} km`
  const minutes = Math.round(leg.durationMs / 60_000)
  if (minutes <= 0) return km
  return `${km} · ${minutes} min`
}

export const TRAVEL_LEG_LABELS = {
  arrival: 'Arrival',
  during: 'During session',
  departure: 'Departure',
} as const

/**
 * Plain-text travel block appended to the copied progress note — a factual
 * footer kept SEPARATE from the AI-written note (the model never sees or
 * writes travel figures). Returns null when there is nothing to report.
 *
 * Departure (consistent with the card, and never double-counting):
 * - assumed → the assumed figure, explicitly labelled
 * - linked  → a cross-reference line WITHOUT km — the kilometres appear
 *   exactly once, as the next session's arrival in ITS block
 */
export function formatTravelClipboardBlock(
  travel: SessionTravel | null | undefined,
): string | null {
  if (!hasAnyTravelLeg(travel)) return null
  const lines: string[] = ['Travel:']
  if (travel?.arrival) {
    if (travel.arrival.excluded) {
      lines.push(`- Arrival: excluded (0 km) — ${REMOTE_ARRIVAL_EXCLUSION_LABEL}`)
    } else {
      lines.push(`- Arrival: ${formatTravelLeg(travel.arrival)} (tracked — lower bound)`)
    }
  }
  if (travel?.during) {
    lines.push(`- During session: ${formatTravelLeg(travel.during)} (tracked — lower bound)`)
  }
  const resolution = travel?.departureResolution ?? null
  if (resolution?.kind === 'assumed') {
    lines.push(`- Departure (assumed, same as arrival): ${formatTravelLeg(resolution.leg)}`)
  } else if (resolution?.kind === 'next-session') {
    const when =
      resolution.nextSessionStart != null
        ? `the ${formatClockTime(resolution.nextSessionStart)} session`
        : 'the next session'
    lines.push(`- Departure: to next session — counted once, as ${when}'s arrival`)
  }
  return lines.join('\n')
}
